import { useState, useCallback, useRef, useEffect } from 'react'

const getSvgCoords = (event, svgElement) => {
  const rect = svgElement.getBoundingClientRect()
  return [event.clientX - rect.left, event.clientY - rect.top]
}

export const useLassoDrawing = ({ initialPoints }) => {
  const isInitiallyClosed = initialPoints?.length > 0

  const [points, setPoints] = useState(() => initialPoints || [])
  const [mousePos, setMousePos] = useState(null)
  const [drawing, setDrawing] = useState(!isInitiallyClosed)
  const [closed, setClosed] = useState(isInitiallyClosed)
  const [isDragging, setIsDragging] = useState(false)

  const svgRef = useRef(null)
  const draggingIndexRef = useRef(null)

  const closePolygon = useCallback(() => {
    setClosed(true)
    setDrawing(false)
    setMousePos(null)
  }, [])

  const handleSvgMouseUp = useCallback(
    e => {
      if (!drawing || closed) return
      const svgEl = svgRef.current
      if (!svgEl) return

      const isHandle = e.target.dataset.isHandle === 'true'

      if (isHandle) {
        if (points.length > 2) closePolygon()
        return
      }

      const coords = getSvgCoords(e, svgEl)
      setPoints(prev => [...prev, coords])
    },
    [drawing, closed, points.length, closePolygon]
  )

  const handleContextMenu = useCallback(
    e => {
      if (!drawing || closed || points.length < 3) return
      e.preventDefault()
      closePolygon()
    },
    [drawing, closed, points.length, closePolygon]
  )

  const handleSvgMouseMove = useCallback(
    e => {
      if (!drawing || closed) return

      const svgEl = svgRef.current

      if (!svgEl) return

      setMousePos(getSvgCoords(e, svgEl))
    },
    [drawing, closed]
  )

  const removeLastDot = useCallback(() => {
    if (!drawing || points.length === 0) return

    const newPoints = points.slice(0, -1)
    setPoints(newPoints)

    if (newPoints.length === 0) {
      setDrawing(false)
      setMousePos(null)
    }
  }, [drawing, points])

  const handleCircleMouseDown = useCallback(
    (index, e) => {
      if (!closed) return

      e.stopPropagation()
      e.preventDefault()

      draggingIndexRef.current = index
      setIsDragging(true)
    },
    [closed]
  )

  useEffect(() => {
    if (!closed) return

    const handleWindowMouseMove = e => {
      const idx = draggingIndexRef.current
      if (idx === null || idx === undefined) return

      const svgEl = svgRef.current
      if (!svgEl) return

      const rect = svgEl.getBoundingClientRect()
      const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width)
      const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height)

      setPoints(prev => prev.map((pt, i) => (i === idx ? [x, y] : pt)))
      setMousePos([x, y])
    }

    const handleWindowMouseUp = () => {
      if (draggingIndexRef.current !== null) {
        draggingIndexRef.current = null

        setIsDragging(false)
        setMousePos(null)
      }
    }

    window.addEventListener('mousemove', handleWindowMouseMove)
    window.addEventListener('mouseup', handleWindowMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove)
      window.removeEventListener('mouseup', handleWindowMouseUp)
    }
  }, [closed])

  return {
    points,
    mousePos,
    drawing,
    closed,
    isDragging,
    svgRef,
    handleSvgMouseUp,
    handleContextMenu,
    handleSvgMouseMove,
    handleCircleMouseDown,
    removeLastDot
  }
}
